import dayjs from "dayjs";
import { MageXPatient } from "@prisma/client";
import { Trans, useLingui } from "@lingui/macro";
import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import type { RevisedCorrection } from "@app/utils/correction-gesture";
import { errorClassOption } from "@app/utils/correction-classes";
import { formatValue } from "@app/utils/format-field-value";
import { labelledValue } from "@app/utils/value-labels";
import { asTerms } from "@app/utils/problem-list";

interface RevisedCorrectionRecapProps {
  correction: RevisedCorrection;
  /** The form below is this correction, pre-filled; otherwise it is only shown, as it was made. */
  revising: boolean;
  property: keyof MageXPatient;
  type?: TypeEnum;
  schema: PatientSchema;
  /** The value the correction gave. */
  correctedValue: unknown;
}

/**
 * The correction a reopened modal starts from: when, by whom, and what it replaced (PT-165). When
 * a later document has replaced its value, its reason and text are shown here, since the form
 * below starts from the value on screen.
 */
export default function RevisedCorrectionRecap({
  correction,
  revising,
  property,
  type,
  schema,
  correctedValue,
}: RevisedCorrectionRecapProps) {
  const { t } = useLingui();
  const labels = schema[property]?.value_labels;
  // A list of terms reads as the row reads it, a reality by its label; a series of dated scores is
  // not named here.
  const named = (raw: unknown): string | null => {
    const value = asTerms(raw);
    if (Array.isArray(value) && (type === "array_string" || type === "array_enum" || type === "scores")) {
      if (!value.every((item) => typeof item === "string")) return null;
      const terms = labelledValue(labels, value as string[]);
      return terms.length > 0 ? terms.join(", ") : t`Non déterminé`;
    }
    return formatValue(labelledValue(labels, value), property, type) || t`Non déterminé`;
  };
  const date = dayjs(`${correction.madeAt.slice(0, 19)}Z`).format("DD/MM/YYYY");
  const name = correction.authorName;
  const flagged = correction.verdict === "flagged";
  const replaced = named(correction.replacedValue);
  const corrected = named(correctedValue);

  const title = correction.byReader
    ? flagged
      ? t`Votre signalement du ${date}`
      : t`Votre correction du ${date}`
    : name && correction.authorIsDoctor
      ? flagged
        ? t`Signalement du Dr ${name} du ${date}`
        : t`Correction du Dr ${name} du ${date}`
      : name
        ? flagged
          ? t`Signalement de ${name} du ${date}`
          : t`Correction de ${name} du ${date}`
        : flagged
          ? t`Signalement du ${date}`
          : t`Correction du ${date}`;

  return (
    <div className="grid gap-1.5 rounded-lg border border-primary/20 bg-blue-50 p-3 text-sm">
      <p className="text-xs font-medium text-blue-900">{title}</p>
      {flagged
        ? replaced && (
            <p className="text-slate-700">
              <Trans>Valeur signalée comme inexacte :</Trans>{" "}
              <span className="font-medium text-slate-900">{replaced}</span>
            </p>
          )
        : replaced &&
          corrected && (
            <p className="text-slate-700">
              <span className="text-slate-500 line-through">{replaced}</span>
              <span aria-hidden className="px-1.5 text-slate-400">
                →
              </span>
              <span className="sr-only">
                <Trans>remplacé par</Trans>
              </span>
              <span className="font-medium text-slate-900">{corrected}</span>
            </p>
          )}
      {!revising && (
        <>
          {correction.errorClass && (
            <p className="text-xs text-slate-700">
              <Trans>Raison :</Trans> {t(errorClassOption(correction.errorClass).label)}
            </p>
          )}
          {correction.notes.trim() && (
            <p className="whitespace-pre-line text-xs text-slate-700">
              « {correction.notes.trim()} »
            </p>
          )}
          <p className="text-xs text-slate-500">
            <Trans>
              Un document plus récent a depuis remplacé cette valeur : ce que
              vous enregistrez maintenant part de la valeur affichée.
            </Trans>
          </p>
        </>
      )}
    </div>
  );
}
